import type { Property } from './properties';
import babyCabins from './baby-cabins.json';
import isoYllas from './iso-yllas.json';

/**
 * Static properties array - kept as fallback for client components that
 * have not yet been migrated to receive props from server parents.
 * Prefer getProperties() for server contexts.
 */
export const properties: Property[] = [
  ...(babyCabins as Property[]),
  {
    slug: "levikka-yllas",
    name: "Levikka",
    resort: "yllas",
    status: "live",
    airbnbUrl: "https://www.airbnb.co.uk/rooms/1575400128957937432",
    guests: 6,
    bedrooms: 3,
    bathrooms: 1,
    hasSauna: true,
    hasHotTub: false,
    hasWifi: true,
    hasParking: true,
    tagline: "Spacious cabin with Nordic ski trail access in Ylläs",
    description: "Levikka is your base camp for Ylläs - Finland's highest ski fell with 63 slopes and 330km of cross-country trails. After a day on the mountain, warm up in the private sauna. It's the perfect way to end a Lapland day. In summer, the midnight sun hovers over the fells while endless trails start right from your doorstep.",
    features: ["Nordic ski track access", "3 bedrooms", "Private sauna", "Drying room for ski gear", "Grill hut", "Private decking", "Starlink internet", "Free parking for 3 cars", "Fully equipped kitchen", "Fell views"],
    images: [
      "/images/cabins/levikka-exterior.jpg",
      "/images/cabins/skycabin-14.jpg",
      "/images/cabins/skycabin-18.jpg",
      "/images/cabins/skycabin-04.jpg",
      "/images/cabins/skycabin-06.jpg",
      "/images/cabins/skycabin-17.jpg",
      "/images/cabins/skycabin-07.jpg",
      "/images/cabins/skycabin-13.jpg",
      "/images/cabins/skycabin-01.jpg",
      "/images/cabins/skycabin-02.jpg",
      "/images/cabins/skycabin-11.jpg",
      "/images/cabins/skycabin-12.jpg",
      "/images/cabins/skycabin-16.jpg",
      "/images/cabins/skycabin-08.jpg",
      "/images/cabins/skycabin-22.jpg",
      "/images/cabins/skycabin-03.jpg",
      "/images/cabins/skycabin-05.jpg",
      "/images/cabins/skycabin-09.jpg",
      "/images/cabins/skycabin-15.jpg",
      "/images/cabins/skycabin-19.jpg",
      "/images/cabins/skycabin-20.jpg",
      "/images/cabins/skycabin-21.jpg",
    ],
    nearby: [
      { name: "Nordic/Cross-Country Ski Trails", distance: "0 km", type: "trail" },
      { name: "Ylläs Ski Resort", distance: "15 min drive", type: "slopes" },
      { name: "Gondola Lift", distance: "15 min drive", type: "slopes" },
      { name: "Levi Ski Resort", distance: "40 min drive", type: "slopes" },
      { name: "Pallas-Yllästunturi National Park", distance: "0 km", type: "national-park" },
      { name: "Restaurants & Bars", distance: "0.8 km", type: "restaurant" },
      { name: "Kittilä Airport", distance: "39 km", type: "airport" },
      { name: "Kolari Train Station", distance: "37 km", type: "airport" },
    ],
  },
  {
    slug: "skycabin-levi",
    name: "Skycabin",
    resort: "levi",
    status: "live",
    airbnbUrl: "https://www.airbnb.co.uk/rooms/1303592161405037096",
    guests: 8,
    bedrooms: 3,
    bathrooms: 2,
    hasSauna: true,
    hasHotTub: true,
    hasWifi: true,
    hasParking: true,
    tagline: "Panoramic cabin in the heart of Levi",
    description: "Skycabin is a cosy cabin in Levi with wide fell views, right at the heart of Finland's top ski resort. Wake up to stunning views, ski straight to the slopes, and on clear nights watch the northern lights dance overhead. In summer, the midnight sun paints the sky gold while fell trails start right from your doorstep.",
    features: ["Fell views", "Home spa (sauna & hot tub)", "Short walk to slopes - Nordic & Alpine", "Fireplace", "Fully equipped kitchen", "5G internet", "Free parking for 3 cars", "Garage", "Boot warmers & drying room"],
    images: Array.from({ length: 36 }, (_, i) => `/images/cabins/levikka-${String(i + 1).padStart(2, '0')}.jpg`),
    nearby: [
      { name: "Levi Ski Slopes", distance: "10 min walk / 2 min drive", type: "slopes" },
      { name: "Levi Centre", distance: "5.2 km", type: "shop" },
      { name: "Ylläs Ski Resort", distance: "40 min drive", type: "slopes" },
      { name: "Kittilä Airport", distance: "15 min drive", type: "airport" },
    ],
  },
  {
    slug: "varakka-yllas",
    name: "Varakka",
    resort: "yllas",
    status: "live",
    airbnbUrl: "https://www.airbnb.co.uk/rooms/1604465730266610727",
    guests: 6,
    bedrooms: 3,
    bathrooms: 2,
    hasSauna: true,
    hasHotTub: false,
    hasWifi: true,
    hasParking: true,
    tagline: "Fell-view cabin in Ylläs, at the edge of a national park",
    description: "Varakka sits in the shadow of Ylläs fell - the highest ski fell in Finland. It has 63 slopes, Finland's longest 3km runs, and the Pallas-Yllästunturi National Park on your doorstep. This is Lapland at its most wild and stunning. In summer, hike the ancient fells, bike at Finland's largest bike park, or paddle the clear lakes under the midnight sun.",
    features: ["Fell views", "National park access", "Nordic ski track access", "Grill hut", "Fireplace", "3 bedrooms", "Private sauna", "Drying room for ski gear", "Starlink internet", "Free parking for 3 cars", "Fully equipped kitchen"],
    images: [
      "/images/cabins/varakka-02.jpg",
      "/images/cabins/varakka-10.jpg",
      "/images/cabins/varakka-11.jpg",
      "/images/cabins/varakka-12.jpg",
      "/images/cabins/varakka-05.jpg",
      "/images/cabins/varakka-06.jpg",
      "/images/cabins/varakka-07.jpg",
      "/images/cabins/varakka-01.jpg",
      "/images/cabins/varakka-03.jpg",
      "/images/cabins/varakka-09.jpg",
      "/images/cabins/varakka-04.jpg",
      "/images/cabins/varakka-08.jpg",
    ],
    nearby: [
      { name: "Nordic/Cross-Country Ski Trails", distance: "0 km", type: "trail" },
      { name: "Ylläs Ski Resort", distance: "15 min drive", type: "slopes" },
      { name: "Gondola Lift", distance: "15 min drive", type: "slopes" },
      { name: "Levi Ski Resort", distance: "40 min drive", type: "slopes" },
      { name: "Pallas-Yllästunturi National Park", distance: "0 km", type: "national-park" },
      { name: "Restaurants & Bars", distance: "0.8 km", type: "restaurant" },
      { name: "Kittilä Airport", distance: "39 km", type: "airport" },
      { name: "Kolari Train Station", distance: "37 km", type: "airport" },
    ],
  },
  {
    slug: "aijakka-yllas",
    name: "Aijakka",
    resort: "yllas",
    status: "coming-soon",
    airbnbUrl: "",
    guests: 0,
    bedrooms: 0,
    bathrooms: 0,
    hasSauna: true,
    hasHotTub: false,
    hasWifi: true,
    hasParking: true,
    tagline: "A new Ylläs retreat - coming next season",
    description: "Aijakka is our newest addition to the Ylläs collection. Details coming soon - join the waitlist to be first to book.",
    features: [],
    images: [],
    nearby: [],
  },
  isoYllas.property as Property,
];
